<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from '@/plugins/i18n';
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { formatDate, getYearDateRange } from '@/utils/dateHelpers';
import { differenceInDays } from 'date-fns'
// @ts-ignore
import vClickOutside from 'click-outside-vue3'


const predefinedRanges = {
  'weekly': 7,
  'last30days': 30,
  'last90days': 90,
  'last365days': 365
};

//props
const props = defineProps({
    startDate: { type: Date, default: null, required: false },
    endDate: { type: Date, default: null, required: false },
    rangeLabel: { type: String, default: 'Date Range', required: false },
    placeholder: { type: String, default: 'Time Range', required: false },
    showCount: { type: Boolean, default: false, required: false },
    hasClearAll: { type: Boolean, default: false, required: false },
    closeOnClickOutside: { type: Boolean, default: false, required: false }, //close dropdown on click outside
    iconOpen: { type: String, default: '', required: false },
    iconClose: { type: String, default: '', required: false },
    iconMain: { type: String, default: '', required: false },
    iconOpenMain: { type: String, default: '', required: false  },
    autoSelectRange: { type: Boolean, default: true, required: false  }
});

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

//refs
const isOpen = ref(false);

const showDatePickerFrom = ref(false);
const showDatePickerTo = ref(false);

// helper: get today and 365 days ago
const { start, end } = getYearDateRange();
const dateStart = ref<Date | null>(props.startDate ?? start);
const dateEnd = ref<Date | null>(props.endDate ?? end);

//coumputed
// Disabled ranges for start picker: any date after the current end
const disabledStartDates = computed(() => {
   if (!dateEnd.value) {
    return []; // nothing disabled if end is not set
  }
  return [
    { start: new Date(dateEnd.value.getTime() + 86400000), end: null } // disable all after end
  ];
});

// Disabled ranges for end picker: any date before the current start
const disabledEndDates = computed(() => {
   if (!dateStart.value) {
    return []; // nothing disabled if start is not set
  }
  return [
    { start: null, end: new Date(dateStart.value.getTime() - 86400000) } // disable all before start
  ];
});

//event handlers
const handleHeaderClick = () => {
    isOpen.value = !isOpen.value;
}

const onClickDateFrom = (event: FocusEvent) => {
  showDatePickerFrom.value = !showDatePickerFrom.value;
};

const onClickDateTo = (event: FocusEvent) => {
    showDatePickerTo.value = !showDatePickerTo.value
}   
const setDateRange = (range: string) => {
  const today = new Date();
  let start: Date | null = null;
  let end: Date = today;

  if (range in predefinedRanges) {
    const days = predefinedRanges[range as keyof typeof predefinedRanges];
    start = new Date();
    start.setDate(today.getDate() - days);
  } else {
    start = dateStart.value; // keep current start
  }

  dateStart.value = start;
  dateEnd.value = end;
  emit('change', { start: dateStart.value, end: dateEnd.value })
};

const checkPredefinedRange = computed( () => {
  if(!props.autoSelectRange) {
    return '';
  }
  if (!dateStart.value || !dateEnd.value) {
    return ''; // no match if either is not set
  }

  const diff = differenceInDays(dateEnd.value, dateStart.value);

  const matchedRange = Object.entries(predefinedRanges).find(
    ([, days]) => days === diff
  );

  return matchedRange ? matchedRange[0] : '';
});


const onDayFromClick = (day: any) => {
    showDatePickerFrom.value = false;
    emit('change', { start: dateStart.value, end: dateEnd.value })
};

const onDayToClick = (day: any) => {
    showDatePickerTo.value = false;
    emit('change', { start: dateStart.value, end: dateEnd.value })
}

const handleFromClickOutside = () => {
  showDatePickerFrom.value = false;
}

const handleToClickOutside = () => {
  showDatePickerTo.value = false;
}

const handleToClickOutsideDropdown = () => {
  if(props.closeOnClickOutside) {
    isOpen.value = false;
  }
}

const handleClearAll = () => {
  const { start, end } = getYearDateRange();
  dateStart.value = start;
  dateEnd.value = end;
  showDatePickerFrom.value = false;
  showDatePickerTo.value = false;
  emit('change', { start: dateStart.value, end: dateEnd.value })
}

//directives
const vClickOutsideAction = vClickOutside.directive

//events
const emit = defineEmits<{
  (e: 'change', payload: { start: Date | null; end: Date | null }): void
}>();


//watchers
watch(
  () => [props.startDate, props.endDate],
  ([newStart, newEnd]) => {
    dateStart.value = newStart
    dateEnd.value = newEnd
  },
  { immediate: true }
)
</script>

<template>
    <div :class="['timerange-dropdown', { 'open': isOpen }]" v-click-outside-action="handleToClickOutsideDropdown">
        <div :class="['timerange-dropdown--header', { 'open': isOpen }]" @click="handleHeaderClick">
            <img v-if="isOpen" class="icon-left" :src="iconOpenMain"></img>
            <img v-else class="icon-left" :src="iconMain"></img>
            <div class="placeholder">{{ placeholder }}</div>
            <div class="count"><div>{{ dateStart && dateEnd ?  1 : '' }}</div></div>
            <img v-if="isOpen" class="icon-right" :src="iconOpen"></img>
            <img v-else class="icon-right" :src="iconClose"></img>
        </div>
        <div class="timerange-dropdown--body" v-show="isOpen">
            <div class="timerange count-clear" v-if="showCount || hasClearAll">
                <div class="selected_count" v-if="showCount">{{ dateStart && dateEnd ?  '1 selected' : '' }}</div>
                <div class="clear_selected" @click="handleClearAll" v-if="hasClearAll">{{ t('clearAll') }}</div>
            </div>
            <div class="timerange range-options">
                <div class="timerange-options-row">
                    <div :class="['timerange-item', { selected: checkPredefinedRange === 'weekly' }]" @click="setDateRange('weekly')">{{ t('thisWeek') }}</div>
                    <div :class="['timerange-item', { selected: checkPredefinedRange === 'last30days' }]" @click="setDateRange('last30days')">{{ t('last30Days') }}</div>
                </div>
                <div class="timerange-options-row">
                    <div :class="['timerange-item', { selected: checkPredefinedRange === 'last90days' }]" @click="setDateRange('last90days')">{{ t('last90Days') }}</div>
                    <div :class="['timerange-item', { selected: checkPredefinedRange === 'last365days' }]" @click="setDateRange('last365days')">{{ t('last365Days') }}</div>
                </div>
            </div>
            <div class="timerange daterange">
                <div class="header-label">{{ rangeLabel }}</div>
                <div class="from">
                    <div class="label">{{ t('from') }}</div>
                    <input type="text" :value="formatDate(dateStart)" placeholder="DD/MM/YYYY" readonly  @click="onClickDateFrom" />
                </div>
                <div class='datepicker' v-if="showDatePickerFrom" v-click-outside-action="handleFromClickOutside">
                      <DatePicker
                          v-model="dateStart"
                          @dayclick="onDayFromClick"
                          :disabled-dates="disabledStartDates"
                          />
                  </div>
                <div class="to" >
                    <div class="label">{{ t('to') }}</div>
                    <input type="text" :value="formatDate(dateEnd)" placeholder="DD/MM/YYYY" readonly @click="onClickDateTo" />
                </div>
                <div class='datepicker' v-if="showDatePickerTo"  v-click-outside-action="handleToClickOutside">
                    <DatePicker v-model="dateEnd"
                        @dayclick="onDayToClick"
                        :disabled-dates="disabledEndDates"
                    />
                </div>
            </div>
        </div>
    </div>
</template>